import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedTabs } from '@/components/ui/animated-tabs';
import { RouteTransition } from '@/components/ui/route-transition';
import { GlossyBackground } from '@/components/ui/glossy-background';
import { DashboardOverview } from '@/components/dashboard/DashboardOverview';
// import { SimpleAIDashboard } from '@/components/dashboard/SimpleAIDashboard';
import { EnhancedAIDashboard } from '@/components/dashboard/EnhancedAIDashboard';
import { ComplaintsTab } from '@/components/dashboard/ComplaintsTab';
import { UsersTab } from '@/components/dashboard/UsersTab';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { LogOut } from 'lucide-react';
import ProductsPage from '../products/ProductsPage';
import BatchesPage from '../batches/BatchesPage';

export const DashboardPage: React.FC = () => {
  const { logout } = useAuth(); 
  const location = useLocation();
  const navigate = useNavigate();

  // Extract current tab from URL path
  const pathSegments = location.pathname.split('/');
  const currentPath = pathSegments[pathSegments.length - 1];
  const activeTab = currentPath === 'dashboard' || currentPath === '' ? 'overview' : currentPath;

  const handleTabChange = (tabValue: string) => {
    navigate(`/dashboard/${tabValue}`);
  };

  const tabs = [
    { id: 'overview', label: 'Dashboard', icon: '📊' },
    // { id: 'ai-dashboard', label: 'AI Dashboard', icon: '🤖' },
    { id: 'advanced-dashboard', label: 'Advanced Charts', icon: '📈' },
    { id: 'complaints', label: 'Complaints', icon: '📋' },
    { id: 'products', label: 'Products', icon: '📦' },
    { id: 'batches', label: 'Batches', icon: '🏭' },
    { id: 'users', label: 'Users', icon: '👥' },
  ];

  return (
    <GlossyBackground className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-lg border-b border-white/20 dark:border-gray-700/50">
        <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">Admin Dashboard</h1>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Welcome back, System Administrator</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="flex items-center gap-1 sm:gap-2 text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-white/30 dark:border-gray-600/30 text-xs sm:text-sm px-2 sm:px-3"
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="w-full">
          <AnimatedTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            className="w-full max-w-6xl mx-auto mb-4 sm:mb-8"
          />

          <Routes>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={
              <RouteTransition>
                <DashboardOverview />
              </RouteTransition>
            } />
            {/* <Route path="ai-dashboard" element={
              <RouteTransition>
                <SimpleAIDashboard />
              </RouteTransition>
            } /> */}
            <Route path="advanced-dashboard" element={
              <RouteTransition>
                <EnhancedAIDashboard />
              </RouteTransition>
            } />
            <Route path="complaints" element={
              <RouteTransition>
                <ComplaintsTab />
              </RouteTransition>
            } />
            <Route path="products" element={
              <RouteTransition>
                <ProductsPage />
              </RouteTransition>
            } />
            <Route path="batches" element={
              <RouteTransition>
                <BatchesPage />
              </RouteTransition>
            } />
            <Route path="users" element={
              <RouteTransition>
                <UsersTab />
              </RouteTransition>
            } />
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Routes>
        </div>
      </div>
    </GlossyBackground>
  );
};